import React from 'react';
import { Link } from 'react-router-dom';
import { Layers, Star, ArrowRight } from 'lucide-react';
import { DynamicCTA } from './DynamicCTA';

/**
 * ClusterLinks Component
 * 
 * "Internal Linking System 2.0"
 * Creates a semantic mesh by linking to:
 * 1. The "Pillar" (Hub) of the current cluster.
 * 2. Top related articles in the same category.
 * 
 * Implements the "Cluster Content" strategy for SEO.
 */
export const ClusterLinks = ({ currentArticle, allArticles }) => {
    if (!currentArticle || !allArticles || allArticles.length === 0) return null;

    const category = currentArticle.category;

    // 1. Find the Pillar(s) for this category
    // A pillar is defined by type='pillar' OR if it marks itself as a hub
    // Filters: Same category + isPillar + NOT current article
    const clusterPillars = allArticles.filter(a =>
        a.category === category &&
        (a.layoutType === 'pillar' || a.type === 'pillar') &&
        a.id !== currentArticle.id
    );

    // 2. Find Category Peers (Articles in same bucket)
    // Exclude current article and pillars
    const categoryPeers = allArticles
        .filter(a =>
            a.category === category &&
            a.id !== currentArticle.id &&
            a.layoutType !== 'pillar' &&
            a.type !== 'pillar'
        )
        // Sort by "Editor Pick" or Date, here we just shuffle/slice for variety or take newest
        // For SEO mesh, linking to distinct relevant content is key. 
        // v3.0: Expanded to 6 siblings for stronger semantic mesh
        .sort((a, b) => (b.score || 0) - (a.score || 0))
        .slice(0, 6);

    if (clusterPillars.length === 0 && categoryPeers.length === 0) return null;

    return (
        <section className="my-12">
            {/* Slim CTA specific to this cluster context */}
            <DynamicCTA article={currentArticle} variant="slim" />

            <div className="p-1 rounded-2xl bg-gradient-to-br from-white/5 to-white/0 border border-white/10 overflow-hidden">
                <div className="bg-black/40 backdrop-blur-sm p-6 sm:p-8 rounded-2xl">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-primary/20 rounded-lg">
                            <Layers className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold">Explore {category}</h3>
                            <p className="text-sm text-muted-foreground">Discover more in this topic</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Column 1: The Pillar (Hub) */}
                        {clusterPillars.length > 0 && (
                            <div>
                                <h4 className="text-sm font-semibold text-primary uppercase tracking-wider mb-4 flex items-center gap-2">
                                    <Star className="w-3 h-3" /> Essential Guide
                                </h4>
                                {clusterPillars.map(pillar => (
                                    <Link
                                        key={pillar.id}
                                        to={pillar.canonicalPath || `/blog/${pillar.slug}`}
                                        className="block group relative overflow-hidden rounded-xl border border-primary/20 bg-primary/5 hover:bg-primary/10 transition-all duration-300 p-5"
                                    >
                                        <h5 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">
                                            {pillar.title}
                                        </h5>
                                        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                                            {pillar.excerpt || "The comprehensive guide to this topic."}
                                        </p>
                                        <span className="inline-flex items-center text-sm font-medium text-primary">
                                            Read the Hub <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                                        </span>
                                    </Link>
                                ))}
                            </div>
                        )}

                        {/* Column 2: More in this Category */}
                        {categoryPeers.length > 0 && (
                            <div>
                                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                                    More in {category}
                                </h4>
                                <ul className="space-y-3">
                                    {categoryPeers.map(peer => (
                                        <li key={peer.id}>
                                            <Link
                                                to={peer.canonicalPath || `/blog/${peer.slug}`}
                                                className="flex items-start gap-3 group hover:bg-white/5 p-2 rounded-lg transition-colors"
                                            >
                                                <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-white/20 group-hover:bg-primary transition-colors" />
                                                <div>
                                                    <span className="block font-medium group-hover:text-primary transition-colors line-clamp-1">
                                                        {peer.title}
                                                    </span>
                                                    {peer.readTime && (
                                                        <span className="text-xs text-muted-foreground">
                                                            {peer.readTime} read
                                                        </span>
                                                    )}
                                                </div>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};
